import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { Common } from '../models/common.model';

export enum SortOrder { ASC = -1, DESC = 1 }

@Injectable()
export abstract class CommonDataTableService<DATA extends Common> {

  public itemLength = 100;
  public currentPage = 0;
  public pageLength = 0;

  public sortTarget = 'id';
  public sortOrder = SortOrder.ASC;
  public sortTargets = [];

  public isBulkCheckDisabled = true;
  public readonly datasource = new BehaviorSubject<DATA[]>([]);
  public readonly viewsource = new BehaviorSubject<DATA[]>([]);

  public readonly onPageCountUpdated = new Subject<number>();

  public readonly isLoading = new BehaviorSubject<boolean>(false);
  public readonly loadErrorHandler = new Subject();
  public readonly loadSuccessHandler = new Subject<void>();

  // Datasourceを読み込むための処理
  protected abstract loadDatasource(): Observable<DATA[]>;

  constructor() {
    this.initialize();
  }

  private initialize() {
    this.datasource.pipe(
      // Page数算出
      tap(items => this.pageLength = Math.ceil(items.length / this.itemLength)),
      // 表示中のデータを更新
      tap(this.refreshViewsource.bind(this))
    ).subscribe();

    this.onPageCountUpdated.pipe(
      tap(page => this.currentPage = page),
      tap(_ => this.refreshViewsource())
    ).subscribe();
  }


  public load() {
    this.isLoading.next(true);
    return this.loadDatasource().pipe(
      finalize(() => this.isLoading.next(false)),
      tap(_ => this.currentPage = 0),
      tap((items: DATA[]) => this.datasource.next(items))
    ).subscribe(
      _ => this.loadSuccessHandler.next(),
      e => this.loadErrorHandler.next(e)
    );

  }

  public refreshViewsource(items: DATA[] = this.datasource.value) {
    const idx = this.currentPage * this.itemLength;
    for (let i = 0; i < this.sortTargets.length; i++) {
      items = this.sort(items, this.sortTargets[i], this.sortOrder);
    }
    const viewsource = this.sort(items, this.sortTarget, this.sortOrder)
      .slice(idx, idx + this.itemLength);
    this.viewsource.next(viewsource);

    this.isBulkCheckDisabled = viewsource.length > 0 ? false : true;
  }

  protected sort(items: DATA[], key: string, order: SortOrder) {
    return items
      .sort((a, b) => {
        const aVal = this.getSortValueFromPath(a, key);
        // HACK PF別OSグループのソート項目にブランクを含む項目があるため正しく並び替えするために必要な処理が依存している
        // 空文字の場合は昇順降順問わず下に
        if (aVal ===  '') {
          return 1;
        }
        const bVal = this.getSortValueFromPath(b, key);
        // 空文字の場合は昇順降順問わず下に
        if (bVal === '') {
          return -1;
        }

        if (aVal > bVal) {
          return order * -1;
        }
        if (aVal < bVal) {
          return order * 1;
        }
        return 0;
      });
  }

  protected getSortValueFromPath(obj: any, path: string) {
    return path.split('/')
      .reduce(
        (prev, key) => {
          if (typeof prev[key] === 'string') {
            return prev[key].toUpperCase();
          }
          if (prev[key] instanceof Date) {
            return prev[key].getTime();
          }
          return prev[key];
        },
        obj
      );
  }

  public setSort(target: string, order: SortOrder) {
    this.sortTarget = target;
    this.sortOrder = order;
    this.currentPage = 0;
    return this;
  }

  public setItemLength(itemLength: number) {
    this.itemLength = itemLength;
    this.pageLength = Math.ceil(this.datasource.value.length / this.itemLength);
    this.currentPage = 0;
    return this;
  }

  public setIsBulkCheckDisabled() {
    this.isBulkCheckDisabled = this.viewsource.value.length > 0 ? false : true;
    return this;
  }

  public goto(page: number) {
    this.onPageCountUpdated.next(page);
  }
}
